import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import { Swiper as SwiperClass } from 'swiper/types';
import SliderItem from '@components/Slider/SliderItem';
import styles from '@features/voxbox/sections/Header/components/HeaderSlider/HeaderSlider.styles';
import SliderControlArrow from '@components/Slider/SliderArrowControl';
import SliderIndicator from '@components/Slider/SliderIndicator';
import { ISliderItems } from '@components/typescript';
import 'swiper/css';
import 'swiper/css/effect-fade';
import { useResponsive } from '@features/responsive';
type PropsType = {
  slides: ISliderItems;
  isFirst?: boolean;
};
const HeaderSlider = ({
  slides,
  isFirst = false
}: PropsType) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass>();
  const [currentSlide, setCurrentSlide] = useState(1);
  const autoplayTimeoutRef = useRef<NodeJS.Timeout>(null);
  const {
    headerSliderContainer
  } = styles;
  const {
    isWiderThanTablet
  } = useResponsive();
  const shouldRenderControls = slides.length > 1;
  useEffect(() => {
    if (swiperInstance) {
      autoplayTimeoutRef.current = setTimeout(() => {
        swiperInstance.params.autoplay = {
          delay: 5000,
          disableOnInteraction: false
        };
        swiperInstance.autoplay.start();
      }, 10000);
    }
    return () => {
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
      }
      if (swiperInstance && swiperInstance.autoplay) {
        swiperInstance.autoplay.stop();
      }
    };
  }, [swiperInstance]);
  const onNextClick = () => {
    if (!swiperInstance) return;
    swiperInstance.slideNext();
  };
  const onPrevClick = () => {
    if (!swiperInstance) return;
    swiperInstance.slidePrev();
  };
  const onSlideChange = swiper => {
    const {
      realIndex
    } = swiper;
    setCurrentSlide(realIndex + 1);
  };
  const onIndicatorClick = (indicatorIndex: number) => {
    swiperInstance?.slideTo(indicatorIndex);
  };

  // WCAG 2.1.1 Keyboard - automatyczne przełączanie slajdu gdy focus trafia na element na innym slajdzie
  useEffect(() => {
    if (!swiperInstance) return;
    const handleFocusIn = (event: FocusEvent) => {
      const focusedElement = event.target as HTMLElement;
      if (!focusedElement) return;

      // Sprawdź czy element jest w kontenerze slidera
      const sliderContainer = swiperInstance.el;
      if (!sliderContainer.contains(focusedElement)) return;

      // Znajdź slajd zawierający element z focusem
      const slideContainingFocus = focusedElement.closest('.swiper-slide') as HTMLElement;
      if (!slideContainingFocus) return;

      // Sprawdź czy slajd jest aktywny
      const isActive = slideContainingFocus.classList.contains('swiper-slide-active');
      if (isActive) return;

      // Znajdź indeks slajdu
      const realIndex = swiperInstance.slides.indexOf(slideContainingFocus);
      if (realIndex !== -1) {
        swiperInstance.slideTo(realIndex);
      }
    };
    const sliderContainer = swiperInstance.el;
    sliderContainer.addEventListener('focusin', handleFocusIn);
    return () => {
      sliderContainer.removeEventListener('focusin', handleFocusIn);
    };
  }, [swiperInstance]);
  const renderArrows = () => shouldRenderControls && <>
        <SliderControlArrow direction="left" callback={onPrevClick} />
        <SliderControlArrow direction="right" callback={onNextClick} />
      </>;
  const renderSlides = () => {
    const renderedSlides: JSX.Element[] = [];
    slides.forEach((slide, index) => {
      renderedSlides.push(<SwiperSlide key={slide.itemId}>
          {({
          isVisible
        }) => <SliderItem item={slide} isActive={isVisible} isFirst={isFirst && index === 0} />}
        </SwiperSlide>);
    });
    return renderedSlides;
  };
  return <div sx={headerSliderContainer} data-testid="header-slider">
      <Swiper modules={[EffectFade, Autoplay]} effect="fade" onSwiper={swiper => setSwiperInstance(swiper)} centeredSlides loop fadeEffect={{
      crossFade: true
    }} onActiveIndexChange={onSlideChange} data-testid="header-slider-swiper">
        {renderSlides()}
      </Swiper>
      {isWiderThanTablet && renderArrows()}
      {shouldRenderControls && <SliderIndicator slidesLength={slides.length} current={currentSlide} onClick={onIndicatorClick} />}
    </div>;
};
export default HeaderSlider;